import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import axiosConn from '../../axiosConn';
import { MAX_IMAGE_SIZE } from '../../config/constants';
import { createMediaUploader } from '../../utils/mediaUpload';
import getShareUrl from '../../utils/getShareUrl';
import { getHitLabApiMessage, isHttpsUrl } from './hitLabUtils';

const CATALOG_DEFAULTS = {
  trackId: '',
  kind: 'track',
  status: 'draft',
  title: '',
  artistName: '',
  publicLabel: '',
  audioUrl: '',
  coverArtUrl: '',
  durationSeconds: '',
  displayOrder: '',
  admissionType: 'manual_admin',
  sourceType: 'manual_admin',
  artistContactEmail: '',
  artistPlayerId: '',
  artistPlayerEmailSnapshot: '',
  artistRecipientStatus: 'unmapped',
  artistRecipientUpdatedAt: '',
  rightsStatus: 'pending',
  radioPermissionConfirmed: false,
  adminNotes: '',
};

const STATUS_OPTIONS = [
  ['draft', 'Draft'],
  ['active', 'Active'],
  ['archived', 'Archived'],
];

const KIND_OPTIONS = [
  ['track', 'Track'],
  ['bumper', 'Bumper'],
];

const ADMISSION_OPTIONS = [
  ['manual_admin', 'Manual admin'],
  ['joe_galaxy_owned', 'Joe Galaxy owned'],
  ['curator_free', 'Curator free'],
  ['hitlab_paid', 'HitLab paid'],
  ['promo', 'Promo'],
];

const RIGHTS_OPTIONS = [
  ['pending', 'Pending'],
  ['owner_controlled', 'Owner controlled'],
  ['artist_confirmed', 'Artist confirmed'],
  ['admin_verified', 'Admin verified'],
];

const getCatalogItems = (response) => {
  const items = response?.data?.data?.items;
  return Array.isArray(items) ? items : [];
};

const withParams = (authConfig, params) => ({
  ...(authConfig || {}),
  params,
});

const getFieldValue = (item = {}, field) =>
  item[field] === null || item[field] === undefined ? '' : item[field];

const slugify = (value, fallback = 'radio-item') => {
  const slug = String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);
  return slug || fallback;
};

const getGeneratedTrackId = (form = {}) => {
  if (form.trackId) return form.trackId;
  if (form.kind === 'bumper') {
    return slugify(`bumper ${form.publicLabel || form.title}`, 'bumper');
  }
  return slugify(`${form.artistName} ${form.title}`, 'radio-track');
};

const hasGeneratedTrackIdSource = (form = {}) => {
  if (form.trackId) return true;
  if (form.kind === 'bumper') {
    return Boolean((form.publicLabel || form.title || '').trim());
  }
  return Boolean((form.artistName || '').trim() && (form.title || '').trim());
};

const getFormFromItem = (item = {}) => ({
  ...CATALOG_DEFAULTS,
  trackId: getFieldValue(item, 'trackId'),
  kind: getFieldValue(item, 'kind') || 'track',
  status: getFieldValue(item, 'status') || 'draft',
  title: getFieldValue(item, 'title'),
  artistName: getFieldValue(item, 'artistName'),
  publicLabel: getFieldValue(item, 'publicLabel'),
  audioUrl: getFieldValue(item, 'audioUrl'),
  coverArtUrl: getFieldValue(item, 'coverArtUrl'),
  durationSeconds: getFieldValue(item, 'durationSeconds'),
  displayOrder: getFieldValue(item, 'displayOrder'),
  admissionType: getFieldValue(item, 'admissionType') || 'manual_admin',
  sourceType: getFieldValue(item, 'sourceType') || 'manual_admin',
  artistContactEmail: getFieldValue(item, 'artistContactEmail'),
  artistPlayerId: getFieldValue(item, 'artistPlayerId'),
  artistPlayerEmailSnapshot: getFieldValue(item, 'artistPlayerEmailSnapshot'),
  artistRecipientStatus:
    getFieldValue(item, 'artistRecipientStatus') || 'unmapped',
  artistRecipientUpdatedAt: getFieldValue(item, 'artistRecipientUpdatedAt'),
  rightsStatus: getFieldValue(item, 'rightsStatus') || 'pending',
  radioPermissionConfirmed: Boolean(item.radioPermissionConfirmed),
  adminNotes: getFieldValue(item, 'adminNotes'),
});

const RECIPIENT_STATUS_LABELS = {
  unmapped: 'Unmapped',
  email_only: 'Email only',
  linked: 'Linked',
};

const getRecipientStatusLabel = (status) =>
  RECIPIENT_STATUS_LABELS[status] || RECIPIENT_STATUS_LABELS.unmapped;

const getCatalogItemLabel = (item = {}) =>
  String(item.title || item.publicLabel || item.trackId || 'Radio item').trim();

const getSongAdRotationSummary = (item = {}) => item.songAdRotation || {};

const isPaidSongAdActive = (item = {}) =>
  getSongAdRotationSummary(item).paid?.active === true;

const isAdminSongAdGrantActive = (item = {}) =>
  getSongAdRotationSummary(item).adminGrant?.active === true;

const isSongAdGrantEligible = (item = {}) =>
  getSongAdRotationSummary(item).catalogAdSafe === true ||
  (getSongAdRotationSummary(item).catalogAdSafe === undefined &&
    item.kind === 'track' &&
    item.status === 'active' &&
    Boolean(item.title) &&
    Boolean(item.artistName) &&
    /^https:\/\//i.test(String(item.coverArtUrl || '').trim()) &&
    item.rightsStatus === 'admin_verified' &&
    item.radioPermissionConfirmed === true);

const getSongAdGrantStatusLabel = (item = {}) => {
  if (isPaidSongAdActive(item)) return 'Paid active';
  if (isAdminSongAdGrantActive(item)) return 'Admin active';
  const summary = getSongAdRotationSummary(item);
  if (summary.adminGrant?.status === 'disabled') return 'Admin disabled';
  if (!isSongAdGrantEligible(item)) return 'Ineligible';
  return 'Not enabled';
};

const getArtistInviteSummary = (item = {}) => item.artistInvite || {};

const buildArtistInviteShareUrl = (payload = {}) => {
  const inviteShareUrl = String(payload.inviteShareUrl || '').trim();
  if (inviteShareUrl) return inviteShareUrl;
  const inviteUrl = String(payload.inviteUrl || '').trim();
  if (!inviteUrl) return '';
  try {
    const parsed = new URL(inviteUrl);
    const token = String(parsed.searchParams.get('artistInvite') || '').trim();
    return token ? getShareUrl('radio-artist-invite', token) : '';
  } catch (err) {
    return '';
  }
};

const isArtistConnected = (item = {}) =>
  item.kind === 'track' &&
  item.artistRecipientStatus === 'linked' &&
  Boolean(item.artistPlayer || item.artistPlayerId);

const canRunFeedbackFanoutVerification = (item = {}) =>
  item.kind === 'track' &&
  item.status === 'active' &&
  item.artistRecipientStatus === 'linked' &&
  Boolean(item.artistPlayer);

const getTrackIdOrder = (catalogItems = []) =>
  catalogItems.map((item) => item.trackId).filter(Boolean);

const hasSameTrackIdOrder = (left = [], right = []) => {
  const leftIds = getTrackIdOrder(left);
  const rightIds = getTrackIdOrder(right);
  return (
    leftIds.length === rightIds.length &&
    leftIds.every((trackId, index) => trackId === rightIds[index])
  );
};

const moveItem = (catalogItems = [], fromIndex, toIndex) => {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= catalogItems.length ||
    toIndex >= catalogItems.length ||
    fromIndex === toIndex
  ) {
    return catalogItems;
  }
  const nextItems = [...catalogItems];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
};

export default function HitLabRadioCatalogManager({ authConfig }) {
  const uploaderRef = useRef(null);
  const [items, setItems] = useState([]);
  const [orderDraftItems, setOrderDraftItems] = useState(null);
  const [filters, setFilters] = useState({ status: '', kind: '' });
  const [form, setForm] = useState(CATALOG_DEFAULTS);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orderSaving, setOrderSaving] = useState(false);
  const [verificationSavingKey, setVerificationSavingKey] = useState('');
  const [songAdGrantSavingKey, setSongAdGrantSavingKey] = useState('');
  const [artistInviteSavingKey, setArtistInviteSavingKey] = useState('');
  const [artistClaimMappingKey, setArtistClaimMappingKey] = useState('');
  const [songAdGrantReasons, setSongAdGrantReasons] = useState({});
  const [productionTestSurface, setProductionTestSurface] = useState('dock');
  const [productionTestRoute, setProductionTestRoute] = useState('/home');
  const [draggingTrackId, setDraggingTrackId] = useState('');
  const [dragOverTrackId, setDragOverTrackId] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const uploader = useMemo(() => {
    if (!uploaderRef.current) {
      uploaderRef.current = createMediaUploader({
        axiosInstance: axios,
        maxImageBytes: MAX_IMAGE_SIZE,
        videoMimeTypes: new Set(),
        isVideoName: () => false,
        forceAutoResourceType: true,
        enableVideoMetaCheck: false,
        enableThumbnails: false,
      });
    }
    return uploaderRef.current;
  }, []);

  const loadCatalog = useCallback(async () => {
    if (!authConfig) return;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.kind) params.kind = filters.kind;
      const response = await axiosConn.get(
        'hitlab/admin/radio/catalog',
        withParams(authConfig, params),
      );
      setItems(getCatalogItems(response));
      setOrderDraftItems(null);
      setDraggingTrackId('');
      setDragOverTrackId('');
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Radio catalog could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [authConfig, filters.kind, filters.status]);

  useEffect(() => {
    void loadCatalog();
  }, [loadCatalog]);

  useEffect(
    () => () => {
      if (coverPreviewUrl) URL.revokeObjectURL?.(coverPreviewUrl);
    },
    [coverPreviewUrl],
  );

  const updateForm = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateCatalogItem = useCallback((trackId, patch) => {
    if (!trackId) return;
    const applyPatch = (entry) =>
      entry.trackId === trackId ? { ...entry, ...patch } : entry;
    setItems((current) => current.map(applyPatch));
    setOrderDraftItems((current) => (current ? current.map(applyPatch) : current));
  }, []);

  const generatedTrackId = getGeneratedTrackId(form);
  const showGeneratedTrackId = hasGeneratedTrackIdSource(form);
  const generatedTrackIdPlaceholder =
    form.kind === 'bumper'
      ? 'Generated after label or title'
      : 'Generated after artist and title';

  const updateFilter = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
    setOrderDraftItems(null);
    setDraggingTrackId('');
    setDragOverTrackId('');
  };

  const clearCoverFile = () => {
    if (coverPreviewUrl) URL.revokeObjectURL?.(coverPreviewUrl);
    setCoverFile(null);
    setCoverPreviewUrl('');
    setUploadProgress(0);
  };

  const setCoverImageFile = (file) => {
    setError('');
    if (!file) return;
    try {
      uploader.validate(file);
      clearCoverFile();
      setCoverFile(file);
      setCoverPreviewUrl(URL.createObjectURL?.(file) || '');
    } catch (err) {
      setError(err?.message || 'Cover art image could not be used.');
    }
  };

  const handleCoverPaste = (event) => {
    const file = Array.from(event.clipboardData?.files || []).find((entry) =>
      String(entry.type || '').startsWith('image/'),
    );
    if (file) setCoverImageFile(file);
  };

  const handleCoverDrop = (event) => {
    event.preventDefault();
    const file = Array.from(event.dataTransfer?.files || []).find((entry) =>
      String(entry.type || '').startsWith('image/'),
    );
    if (file) setCoverImageFile(file);
  };

  const resetForm = () => {
    setForm(CATALOG_DEFAULTS);
    clearCoverFile();
  };

  const validateForm = () => {
    if (!form.title.trim()) return 'Title is required.';
    if (!isHttpsUrl(form.audioUrl)) return 'Audio URL must start with https://.';
    if (form.coverArtUrl && !isHttpsUrl(form.coverArtUrl)) {
      return 'Cover art URL must start with https://.';
    }
    if (form.status === 'active') {
      if (form.rightsStatus === 'pending') {
        return 'Active catalog items require approved rights.';
      }
      if (!form.radioPermissionConfirmed) {
        return 'Active catalog items require Radio permission.';
      }
      if (form.kind === 'track') {
        if (!form.artistName.trim()) return 'Active tracks require artist name.';
        if (!coverFile && !form.coverArtUrl.trim()) {
          return 'Active tracks require cover art.';
        }
      }
    }
    return '';
  };

  const submitCatalogItem = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    if (!authConfig) {
      setError('Please sign in again to continue your artist intake.');
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setUploadProgress(0);
    try {
      let coverArtUrl = form.coverArtUrl.trim();
      if (coverFile) {
        const upload = await uploader.upload(coverFile, {
          publicIdPrefix: 'HitLabRadio/CoverArt',
          onProgress: setUploadProgress,
        });
        coverArtUrl = upload.transformedUrl || upload.secureUrl || coverArtUrl;
      }

      const payload = {
        kind: form.kind,
        status: form.status,
        title: form.title.trim(),
        artistName: form.kind === 'track' ? form.artistName.trim() : '',
        publicLabel:
          form.kind === 'bumper'
            ? (form.publicLabel || form.title).trim()
            : '',
        audioUrl: form.audioUrl.trim(),
        coverArtUrl,
        durationSeconds: Number(form.durationSeconds) || 0,
        admissionType: form.admissionType,
        sourceType: 'manual_admin',
        artistContactEmail:
          form.kind === 'track' ? form.artistContactEmail.trim() : '',
        artistPlayerId:
          form.kind === 'track' ? form.artistPlayerId.trim() : '',
        rightsStatus: form.rightsStatus,
        radioPermissionConfirmed: Boolean(form.radioPermissionConfirmed),
        supportEligible: false,
        adminNotes: form.adminNotes,
      };
      if (String(form.displayOrder).trim()) {
        payload.displayOrder = Number(form.displayOrder) || 0;
      }
      if (String(form.trackId).trim()) {
        payload.trackId = form.trackId.trim();
      }

      const response = await axiosConn.post(
        'hitlab/admin/radio/catalog/upsert',
        payload,
        authConfig,
      );
      const savedItem = response?.data?.data?.item || {};
      const savedTrackId =
        savedItem.trackId || form.trackId || generatedTrackId;
      setMessage('Radio catalog item saved.');
      clearCoverFile();
      setForm((current) => ({
        ...current,
        trackId: savedTrackId,
        coverArtUrl,
        artistContactEmail: getFieldValue(savedItem, 'artistContactEmail'),
        artistPlayerId: getFieldValue(savedItem, 'artistPlayerId'),
        artistPlayerEmailSnapshot: getFieldValue(
          savedItem,
          'artistPlayerEmailSnapshot',
        ),
        artistRecipientStatus:
          getFieldValue(savedItem, 'artistRecipientStatus') ||
          current.artistRecipientStatus,
        artistRecipientUpdatedAt: getFieldValue(
          savedItem,
          'artistRecipientUpdatedAt',
        ),
      }));
      await loadCatalog();
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Radio catalog item could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  const editItem = (item) => {
    setError('');
    setMessage('');
    clearCoverFile();
    setForm(getFormFromItem(item));
  };

  const filtersClear = !filters.status && !filters.kind;
  const visibleItems = orderDraftItems || items;
  const orderDirty =
    Boolean(orderDraftItems) && !hasSameTrackIdOrder(orderDraftItems, items);

  const updateOrderDraft = (updater) => {
    if (!filtersClear) return;
    setOrderDraftItems((current) => {
      const source = current || items;
      const next = updater(source);
      return hasSameTrackIdOrder(next, items) ? null : next;
    });
  };

  const moveOrderItem = (trackId, direction) => {
    updateOrderDraft((source) => {
      const fromIndex = source.findIndex((item) => item.trackId === trackId);
      return moveItem(source, fromIndex, fromIndex + direction);
    });
  };

  const updateSongAdGrantReason = (trackId, value) => {
    setSongAdGrantReasons((current) => ({
      ...current,
      [trackId]: value,
    }));
  };

  const toggleSongAdRotationGrant = async (item, enabled) => {
    if (!authConfig || !item?.trackId) return;
    const reason = String(songAdGrantReasons[item.trackId] || '').trim();
    if (enabled && !reason) {
      setError('Grant reason is required before enabling Song Ad Rotation.');
      setMessage('');
      return;
    }

    setError('');
    setMessage('');
    setSongAdGrantSavingKey(item.trackId);
    try {
      const response = await axiosConn.post(
        'hitlab/admin/radio/catalog/song-ad-rotation-grant',
        {
          trackId: item.trackId,
          enabled,
          ...(enabled ? { grantReason: reason } : {}),
          ...(!enabled && reason ? { disabledReason: reason } : {}),
        },
        authConfig,
      );
      const nextSummary = response?.data?.data?.songAdRotation;
      setItems((current) =>
        current.map((entry) =>
          entry.trackId === item.trackId && nextSummary
            ? { ...entry, songAdRotation: nextSummary }
            : entry,
        ),
      );
      setOrderDraftItems((current) =>
        current
          ? current.map((entry) =>
              entry.trackId === item.trackId && nextSummary
                ? { ...entry, songAdRotation: nextSummary }
                : entry,
            )
          : current,
      );
      if (!enabled) {
        updateSongAdGrantReason(item.trackId, '');
      }
      setMessage(
        enabled
          ? 'Song Ad Rotation admin grant enabled.'
          : 'Song Ad Rotation admin grant disabled.',
      );
    } catch (err) {
      setError(
        getHitLabApiMessage(
          err,
          'Song Ad Rotation admin grant could not be updated.',
        ),
      );
    } finally {
      setSongAdGrantSavingKey('');
    }
  };

  const copyArtistInviteLink = async (item) => {
    if (!authConfig || !item?.trackId || isArtistConnected(item)) return;
    setError('');
    setMessage('');
    setArtistInviteSavingKey(item.trackId);
    try {
      const response = await axiosConn.post(
        `hitlab/admin/radio/catalog/${encodeURIComponent(item.trackId)}/artist-invite`,
        {},
        authConfig,
      );
      const inviteShareUrl = buildArtistInviteShareUrl(response?.data?.data);
      if (!inviteShareUrl || !navigator.clipboard?.writeText) {
        throw new Error('Clipboard is unavailable.');
      }
      await navigator.clipboard.writeText(inviteShareUrl);
      const artistInvite = response?.data?.data?.artistInvite;
      if (artistInvite) updateCatalogItem(item.trackId, { artistInvite });
      setMessage('Artist invite link copied.');
    } catch (err) {
      setError(
        getHitLabApiMessage(err, 'Artist invite link could not be copied.'),
      );
    } finally {
      setArtistInviteSavingKey('');
    }
  };

  const mapArtistClaim = async (item) => {
    const artistInvite = getArtistInviteSummary(item);
    const claimedPlayerId = String(artistInvite.claimedPlayerId || '').trim();
    if (!authConfig || !item?.trackId || !claimedPlayerId || isArtistConnected(item)) {
      return;
    }
    const confirmed = confirmAction(
      `Map @${claimedPlayerId} to this Radio track for JGK support and artist notifications?`,
    );
    if (!confirmed) return;

    setError('');
    setMessage('');
    setArtistClaimMappingKey(item.trackId);
    try {
      const response = await axiosConn.post(
        `hitlab/admin/radio/catalog/${encodeURIComponent(item.trackId)}/map-artist-claim`,
        {},
        authConfig,
      );
      const mappedItem = response?.data?.data?.item || {};
      const artistInviteNext =
        mappedItem.artistInvite || response?.data?.data?.artistInvite;
      updateCatalogItem(item.trackId, {
        artistPlayerId: mappedItem.artistPlayerId || claimedPlayerId,
        artistRecipientStatus: mappedItem.artistRecipientStatus || 'linked',
        ...(artistInviteNext ? { artistInvite: artistInviteNext } : {}),
      });
      setMessage(`Artist claim mapped to @${mappedItem.artistPlayerId || claimedPlayerId}.`);
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Artist claim could not be mapped.'));
    } finally {
      setArtistClaimMappingKey('');
    }
  };

  const handleOrderDragStart = (event, trackId) => {
    if (!filtersClear) return;
    setDraggingTrackId(trackId);
    setDragOverTrackId('');
    event.dataTransfer?.setData('text/plain', trackId);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  };

  const handleOrderDragOver = (event, trackId) => {
    if (!filtersClear || !draggingTrackId) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    setDragOverTrackId(trackId);
  };

  const handleOrderDrop = (event, targetTrackId) => {
    if (!filtersClear) return;
    event.preventDefault();
    const sourceTrackId =
      event.dataTransfer?.getData('text/plain') || draggingTrackId;
    setDraggingTrackId('');
    setDragOverTrackId('');
    if (!sourceTrackId || sourceTrackId === targetTrackId) return;
    updateOrderDraft((source) => {
      const fromIndex = source.findIndex((item) => item.trackId === sourceTrackId);
      const toIndex = source.findIndex((item) => item.trackId === targetTrackId);
      return moveItem(source, fromIndex, toIndex);
    });
  };

  const handleOrderDragEnd = () => {
    setDraggingTrackId('');
    setDragOverTrackId('');
  };

  const revertOrder = () => {
    setOrderDraftItems(null);
    setDraggingTrackId('');
    setDragOverTrackId('');
  };

  const saveOrder = async () => {
    if (!authConfig || !orderDirty || !orderDraftItems) return;
    setOrderSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await axiosConn.post(
        'hitlab/admin/radio/catalog/reorder',
        { trackIds: getTrackIdOrder(orderDraftItems) },
        authConfig,
      );
      setItems(getCatalogItems(response));
      setOrderDraftItems(null);
      setMessage('Order saved.');
    } catch (err) {
      setError(getHitLabApiMessage(err, 'Radio catalog order could not be saved.'));
    } finally {
      setOrderSaving(false);
    }
  };

  const confirmAction = (text) => {
    if (typeof window === 'undefined' || typeof window.confirm !== 'function') {
      return true;
    }
    return window.confirm(text);
  };

  const runProductionFeedbackTest = async (item) => {
    if (!authConfig || !canRunFeedbackFanoutVerification(item)) return;
    const confirmed = confirmAction(
      'This creates a real production-path feedback event from the configured test listener, credits listener JGK, may appear as Radio feedback activity, and sends artist notification fanout. It can only run once per test listener and track.',
    );
    if (!confirmed) return;

    setError('');
    setMessage('');
    setVerificationSavingKey(`production:${item.trackId}`);
    try {
      const surface = productionTestSurface === 'radio' ? 'radio' : 'dock';
      const response = await axiosConn.post(
        'hitlab/admin/radio/feedback-production-flow-test',
        {
          trackId: item.trackId,
          surface,
          route: surface === 'radio' ? '/hitlab/radio' : productionTestRoute,
          confirm: 'RUN_PRODUCTION_FEEDBACK_TEST',
        },
        authConfig,
      );
      const result = response?.data?.data?.result || {};
      setMessage(
        result.message ||
          'Production feedback test requested. Check notification and email queues.',
      );
    } catch (err) {
      setError(
        getHitLabApiMessage(err, 'Production feedback test could not be run.'),
      );
    } finally {
      setVerificationSavingKey('');
    }
  };

  const sendArtistNotificationCanary = async (item) => {
    if (!authConfig || !canRunFeedbackFanoutVerification(item)) return;
    const confirmed = confirmAction(
      'This sends a TEST artist notification/email canary. It does not create listener feedback, JGK, metrics, or public Radio activity.',
    );
    if (!confirmed) return;

    setError('');
    setMessage('');
    setVerificationSavingKey(`canary:${item.trackId}`);
    try {
      const response = await axiosConn.post(
        'hitlab/admin/radio/artist-notification-canary',
        {
          trackId: item.trackId,
          confirm: 'SEND_ARTIST_NOTIFICATION_CANARY',
        },
        authConfig,
      );
      const result = response?.data?.data?.result || {};
      setMessage(
        result.message ||
          'TEST artist notification fanout requested. Check in-app, web push, and email.',
      );
    } catch (err) {
      setError(
        getHitLabApiMessage(err, 'TEST artist notification could not be sent.'),
      );
    } finally {
      setVerificationSavingKey('');
    }
  };

  return (
    <section className="hitlab-radio-catalog-admin" aria-label="Joe Galaxy Radio Catalog">
      <div className="hitlab-radio-catalog-admin-header">
        <div>
          <p className="hitlab-kicker">RADIO CATALOG</p>
          <h2>Joe Galaxy Radio Catalog</h2>
          <p>
            Add approved songs and bumper MP3s to Joe Galaxy Radio using hosted
            HTTPS audio URLs and optional cover art.
          </p>
        </div>
        <button
          type="button"
          className="hitlab-secondary-btn"
          onClick={loadCatalog}
          disabled={loading}
        >
          Refresh catalog
        </button>
      </div>

      {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
      {message ? (
        <div className="hitlab-alert hitlab-alert-success">{message}</div>
      ) : null}

      <div className="hitlab-radio-catalog-grid">
        <form className="hitlab-radio-catalog-form" onSubmit={submitCatalogItem}>
          <div className="hitlab-radio-catalog-form-top">
            <h3>Catalog item</h3>
            <button
              type="button"
              className="hitlab-table-action"
              onClick={resetForm}
            >
              New item
            </button>
          </div>

          <div className="hitlab-listen-form-grid">
            <label>
              Generated track ID
              <input
                value={showGeneratedTrackId ? generatedTrackId : ''}
                placeholder={generatedTrackIdPlaceholder}
                maxLength="120"
                readOnly
              />
            </label>
            <label>
              Kind
              <select
                value={form.kind}
                onChange={(event) => updateForm('kind', event.target.value)}
              >
                {KIND_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select
                value={form.status}
                onChange={(event) => updateForm('status', event.target.value)}
              >
                {STATUS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <div className="hitlab-radio-catalog-readonly hitlab-form-wide">
              Use Draft to pause rotation temporarily. Use Archived for retired
              items.
            </div>
            <label>
              Title
              <input
                value={form.title}
                maxLength="160"
                onChange={(event) => updateForm('title', event.target.value)}
                required
              />
            </label>
            <label>
              Artist name
              <input
                value={form.artistName}
                maxLength="120"
                onChange={(event) => updateForm('artistName', event.target.value)}
                disabled={form.kind === 'bumper'}
              />
            </label>
            {form.kind === 'bumper' ? (
              <label>
                Bumper display label
                <input
                  value={form.publicLabel}
                  maxLength="160"
                  onChange={(event) =>
                    updateForm('publicLabel', event.target.value)
                  }
                />
              </label>
            ) : null}
            <fieldset
              className="hitlab-radio-recipient-section hitlab-form-wide"
              disabled={form.kind === 'bumper'}
            >
              <legend>Artist recipient</legend>
              <p>Notifications go to this email.</p>
              <p>JGK support credits go to this Player ID when linked.</p>
              <p>
                If no Player ID is linked yet, future support credits can wait
                under this artist email.
              </p>
              <div className="hitlab-radio-recipient-grid">
                <label>
                  Artist notification email
                  <input
                    type="email"
                    value={form.artistContactEmail}
                    maxLength="254"
                    onChange={(event) =>
                      updateForm('artistContactEmail', event.target.value)
                    }
                  />
                </label>
                <label>
                  Artist Player ID / JGK wallet recipient
                  <input
                    value={form.artistPlayerId}
                    maxLength="120"
                    onChange={(event) =>
                      updateForm('artistPlayerId', event.target.value)
                    }
                  />
                </label>
              </div>
              <div className="hitlab-radio-recipient-status">
                <span>Recipient status</span>
                <strong>
                  {getRecipientStatusLabel(form.artistRecipientStatus)}
                </strong>
                {form.artistPlayerEmailSnapshot ? (
                  <small>{form.artistPlayerEmailSnapshot}</small>
                ) : null}
              </div>
            </fieldset>
            <label className="hitlab-form-wide">
              Audio URL
              <input
                type="url"
                value={form.audioUrl}
                onChange={(event) => updateForm('audioUrl', event.target.value)}
                required
              />
            </label>
            <label className="hitlab-form-wide">
              Cover art URL
              <input
                type="url"
                value={form.coverArtUrl}
                onChange={(event) => updateForm('coverArtUrl', event.target.value)}
              />
            </label>
            <div className="hitlab-radio-catalog-readonly">
              {form.durationSeconds
                ? `${form.durationSeconds} seconds detected`
                : 'Duration auto-detects from audio.'}
            </div>
            <div className="hitlab-radio-catalog-readonly">
              {form.displayOrder
                ? `Current order ${form.displayOrder}`
                : 'New items append to the end.'}
            </div>
            <label>
              Admission type
              <select
                value={form.admissionType}
                onChange={(event) =>
                  updateForm('admissionType', event.target.value)
                }
              >
                {ADMISSION_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Rights status
              <select
                value={form.rightsStatus}
                onChange={(event) => updateForm('rightsStatus', event.target.value)}
              >
                {RIGHTS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="hitlab-radio-catalog-check">
              <input
                type="checkbox"
                checked={form.radioPermissionConfirmed}
                onChange={(event) =>
                  updateForm('radioPermissionConfirmed', event.target.checked)
                }
              />
              <span>Radio permission confirmed</span>
            </label>
            <div className="hitlab-radio-catalog-readonly">
              Support disabled in V1.
            </div>
            <label className="hitlab-form-wide">
              Admin notes
              <textarea
                value={form.adminNotes}
                maxLength="2000"
                onChange={(event) => updateForm('adminNotes', event.target.value)}
              />
            </label>
          </div>

          <div
            className="hitlab-radio-cover-drop"
            onPaste={handleCoverPaste}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleCoverDrop}
          >
            <label>
              Cover image upload
              <input
                type="file"
                accept="image/*"
                onChange={(event) => setCoverImageFile(event.target.files?.[0])}
              />
            </label>
            {coverPreviewUrl || form.coverArtUrl ? (
              <img
                src={coverPreviewUrl || form.coverArtUrl}
                alt="Radio catalog cover preview"
                width="120"
                height="120"
              />
            ) : null}
            {coverFile ? (
              <button
                type="button"
                className="hitlab-table-action"
                onClick={clearCoverFile}
              >
                Clear cover file
              </button>
            ) : null}
            {uploadProgress > 0 && uploadProgress < 100 ? (
              <span>{uploadProgress}% uploaded</span>
            ) : null}
          </div>

          {form.audioUrl ? (
            <audio
              className="hitlab-radio-catalog-audio"
              src={form.audioUrl}
              controls
              preload="metadata"
              onLoadedMetadata={(event) => {
                const nextDuration = Math.round(
                  Number(event.currentTarget.duration) || 0,
                );
                if (nextDuration > 0) {
                  updateForm('durationSeconds', String(nextDuration));
                }
              }}
            />
          ) : null}

          <div className="hitlab-listen-actions">
            <button
              type="submit"
              className="hitlab-primary-btn"
              disabled={saving}
            >
              {saving ? 'Saving' : 'Save catalog item'}
            </button>
          </div>
        </form>

        <div className="hitlab-radio-catalog-list">
          <div className="hitlab-radio-catalog-order-panel">
            <div>
              <strong>Manual Radio Order</strong>
              <small>Drag items or use Move Up / Move Down.</small>
              <small>
                Draft items are hidden from public rotation. Archived items are
                retired.
              </small>
              {!filtersClear ? (
                <small className="hitlab-radio-catalog-order-hint">
                  Clear filters to reorder the full catalog.
                </small>
              ) : null}
            </div>
            <div className="hitlab-radio-catalog-test-panel">
              <label>
                Feedback test surface
                <select
                  value={productionTestSurface}
                  onChange={(event) =>
                    setProductionTestSurface(event.target.value)
                  }
                >
                  <option value="dock">Dock</option>
                  <option value="radio">Full Radio</option>
                </select>
              </label>
              {productionTestSurface === 'dock' ? (
                <label>
                  Dock test route
                  <input
                    value={productionTestRoute}
                    maxLength="160"
                    onChange={(event) =>
                      setProductionTestRoute(event.target.value)
                    }
                  />
                </label>
              ) : (
                <span className="hitlab-radio-catalog-test-route">
                  /hitlab/radio
                </span>
              )}
            </div>
            {filtersClear && orderDirty ? (
              <div className="hitlab-radio-catalog-order-buttons">
                <button
                  type="button"
                  className="hitlab-primary-btn"
                  onClick={saveOrder}
                  disabled={orderSaving}
                >
                  {orderSaving ? 'Saving order' : 'Save Order'}
                </button>
                <button
                  type="button"
                  className="hitlab-secondary-btn"
                  onClick={revertOrder}
                  disabled={orderSaving}
                >
                  Revert
                </button>
              </div>
            ) : null}
          </div>

          <div className="hitlab-radio-catalog-filters">
            <label>
              Status
              <select
                value={filters.status}
                onChange={(event) => updateFilter('status', event.target.value)}
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Kind
              <select
                value={filters.kind}
                onChange={(event) => updateFilter('kind', event.target.value)}
              >
                <option value="">All kinds</option>
                {KIND_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loading ? <div className="hitlab-alert">Loading Radio catalog...</div> : null}
          <div className="hitlab-radio-catalog-items">
            {visibleItems.map((item, index) => {
              const itemLabel = getCatalogItemLabel(item);
              const canRunFanoutTests =
                canRunFeedbackFanoutVerification(item);
              const showSongAdGrantControl = item.kind === 'track';
              const songAdGrantActive = isAdminSongAdGrantActive(item);
              const songAdGrantEligible = isSongAdGrantEligible(item);
              const songAdGrantSaving =
                songAdGrantSavingKey === item.trackId;
              const songAdGrantStatusLabel = getSongAdGrantStatusLabel(item);
              const artistInvite = getArtistInviteSummary(item);
              const artistConnected = isArtistConnected(item);
              const artistInviteActive = artistInvite.inviteActive === true;
              const artistClaimPending =
                artistInvite.claimPending === true &&
                Boolean(artistInvite.claimedPlayerId);
              const showArtistInviteControl = item.kind === 'track';
              const artistInviteStatusLabel = artistConnected
                ? item.artistPlayerId
                  ? `Connected to @${item.artistPlayerId}`
                  : 'Connected to artist Player'
                : artistClaimPending
                ? `Claim pending: @${artistInvite.claimedPlayerId}`
                : artistInviteActive
                ? 'Artist invite active'
                : 'Not claimed';
              const canMoveUp = filtersClear && index > 0;
              const canMoveDown = filtersClear && index < visibleItems.length - 1;
              const rowClassName = [
                'hitlab-radio-catalog-item',
                filtersClear ? 'hitlab-radio-catalog-item--reorderable' : '',
                draggingTrackId === item.trackId ? 'is-dragging' : '',
                dragOverTrackId === item.trackId ? 'is-drop-target' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
              <article
                key={item.trackId}
                className={rowClassName}
                onDragOver={(event) => handleOrderDragOver(event, item.trackId)}
                onDrop={(event) => handleOrderDrop(event, item.trackId)}
              >
                <div className="hitlab-radio-catalog-order-controls">
                  <span className="hitlab-radio-catalog-order-chip">
                    {index + 1}
                  </span>
                  {filtersClear ? (
                    <>
                      <button
                        type="button"
                        className="hitlab-radio-catalog-drag-handle"
                        aria-label={`Drag to reorder ${itemLabel}`}
                        draggable
                        onDragStart={(event) =>
                          handleOrderDragStart(event, item.trackId)
                        }
                        onDragEnd={handleOrderDragEnd}
                      >
                        Drag
                      </button>
                      <button
                        type="button"
                        className="hitlab-table-action"
                        onClick={() => moveOrderItem(item.trackId, -1)}
                        disabled={!canMoveUp || orderSaving}
                        aria-label={`Move ${itemLabel} up`}
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        className="hitlab-table-action"
                        onClick={() => moveOrderItem(item.trackId, 1)}
                        disabled={!canMoveDown || orderSaving}
                        aria-label={`Move ${itemLabel} down`}
                      >
                        Down
                      </button>
                    </>
                  ) : null}
                </div>
                <div className="hitlab-radio-catalog-meta">
                  <span>{item.kind}</span>
                  <strong>{item.title}</strong>
                  <small>{item.trackId}</small>
                  <small>{item.status}</small>
                </div>
                {showSongAdGrantControl ? (
                  <div className="hitlab-song-ad-grant-control">
                    <div className="hitlab-song-ad-grant-heading">
                      <span className="hitlab-song-ad-grant-label">
                        Song Ad Rotation
                      </span>
                      <span
                        className={
                          isPaidSongAdActive(item)
                            ? 'hitlab-song-ad-grant-chip hitlab-song-ad-grant-chip-paid'
                            : 'hitlab-song-ad-grant-chip'
                        }
                      >
                        {songAdGrantStatusLabel}
                      </span>
                    </div>
                    <label className="hitlab-song-ad-grant-check">
                      <input
                        type="checkbox"
                        checked={songAdGrantActive}
                        disabled={
                          songAdGrantSaving ||
                          (!songAdGrantActive && !songAdGrantEligible)
                        }
                        onChange={(event) =>
                          toggleSongAdRotationGrant(
                            item,
                            event.target.checked,
                          )
                        }
                      />
                      <span>Include in JG Universe Song Ad Rotation</span>
                    </label>
                    <small>
                      Admin-granted. Active until unchecked. This does not mark
                      the track as a paid HitLab Plus purchase and does not
                      change Radio Rotation status.
                    </small>
                    <label className="hitlab-song-ad-grant-reason">
                      Grant note
                      <input
                        value={songAdGrantReasons[item.trackId] || ''}
                        maxLength="500"
                        placeholder="Required when enabling"
                        onChange={(event) =>
                          updateSongAdGrantReason(
                            item.trackId,
                            event.target.value,
                          )
                        }
                      />
                    </label>
                    {!songAdGrantEligible ? (
                      <small className="hitlab-song-ad-grant-warning">
                        Active tracks need approved rights, Radio permission,
                        artist name, and cover art before Song Ad Rotation.
                      </small>
                    ) : null}
                  </div>
                ) : null}
                {showArtistInviteControl ? (
                  <div className="hitlab-radio-artist-invite-control">
                    <div className="hitlab-radio-artist-invite-heading">
                      <span className="hitlab-radio-artist-invite-label">
                        Artist claim
                      </span>
                      <span
                        className={
                          artistConnected
                            ? 'hitlab-radio-artist-invite-chip is-connected'
                            : artistClaimPending
                            ? 'hitlab-radio-artist-invite-chip is-pending'
                            : 'hitlab-radio-artist-invite-chip'
                        }
                      >
                        {artistInviteStatusLabel}
                      </span>
                    </div>
                    <small>
                      Send this private link to the artist so they can claim
                      their JG Artist Profile for this record.
                    </small>
                    {!artistConnected && !artistClaimPending ? (
                      <button
                        type="button"
                        className="hitlab-table-action"
                        onClick={() => copyArtistInviteLink(item)}
                        disabled={artistInviteSavingKey === item.trackId}
                      >
                        {artistInviteSavingKey === item.trackId
                          ? 'Copying invite'
                          : 'Copy Artist Invite Link'}
                      </button>
                    ) : null}
                    {!artistConnected && artistClaimPending ? (
                      <button
                        type="button"
                        className="hitlab-table-action"
                        onClick={() => mapArtistClaim(item)}
                        disabled={artistClaimMappingKey === item.trackId}
                      >
                        {artistClaimMappingKey === item.trackId
                          ? 'Mapping artist'
                          : 'Map artist to this record'}
                      </button>
                    ) : null}
                  </div>
                ) : null}
                <div className="hitlab-radio-catalog-actions">
                  <button
                    type="button"
                    className="hitlab-table-action"
                    onClick={() => editItem(item)}
                  >
                    Edit
                  </button>
                  {canRunFanoutTests ? (
                    <div className="hitlab-radio-catalog-test-actions">
                      <button
                        type="button"
                        className="hitlab-table-action"
                        onClick={() => runProductionFeedbackTest(item)}
                        disabled={
                          verificationSavingKey === `production:${item.trackId}`
                        }
                      >
                        {verificationSavingKey === `production:${item.trackId}`
                          ? 'Running test'
                          : 'Run production feedback test'}
                      </button>
                      <button
                        type="button"
                        className="hitlab-table-action"
                        onClick={() => sendArtistNotificationCanary(item)}
                        disabled={verificationSavingKey === `canary:${item.trackId}`}
                      >
                        {verificationSavingKey === `canary:${item.trackId}`
                          ? 'Sending test'
                          : 'Send test artist notification'}
                      </button>
                    </div>
                  ) : null}
                </div>
              </article>
              );
            })}
            {!visibleItems.length && !loading ? (
              <div className="hitlab-alert">No Radio catalog items yet.</div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
